import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

// Import your logos
import logoNavy from '../assets/logo-main-navy.png';
import logoWhite from '../assets/logo-main-white.png';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Adds a solid background and shrinks the header when scrolling down
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu whenever the route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Since all pages now use a full-bleed hero image, the header is transparent initially everywhere
  const headerBgClass = scrolled ? 'bg-white' : 'bg-transparent';
  
  // DYNAMIC LOGO SIZING
  const logoHeight = scrolled ? '75px' : '100px';

  return (
    <header className={`site-header ${scrolled ? 'scrolled' : ''}`}>
      
      {/* 1. TOP BAR */}
      <div className="header-top-bar">
        <div className="container top-bar-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div className="top-bar-status">
            <span>National Operations Centre | 24/7 Deployment</span>
          </div>
          <div className="top-bar-contact">
            <a href="mailto:info@nightmarkprotectiveservices.com" className="hide-on-mobile">
              <span className="lowercase-email">info@nightmarkprotectiveservices.com</span>
            </a>
            <a href="tel:08001234567">
              01270 446302
            </a>
          </div>
        </div>
      </div>

      {/* 2. MAIN NAVIGATION */}
      <div className={`header-main ${headerBgClass}`} style={{ padding: '0.5rem 0' }}>
        
        {/* INJECTED ENTERPRISE & MOBILE LAYOUT STYLES */}
        <style>
          {`
            .header-container-flex {
              display: flex;
              align-items: center;
              justify-content: space-between;
              width: 100%;
            }
            .main-nav-spaced {
              flex: 1; 
              display: flex;
              justify-content: flex-end; 
              padding-right: 3rem; 
            }
            .main-nav-spaced ul {
              display: flex;
              align-items: center;
              gap: 2.5rem !important; 
              margin: 0;
              padding: 0;
              list-style: none;
            }
            .main-nav-spaced a {
              white-space: nowrap !important;
            }
            .hamburger-btn {
              display: none;
              background: none;
              border: none;
              cursor: pointer;
              padding: 0.5rem;
              z-index: 1001;
            }
            .hamburger-icon {
              display: block;
              width: 25px;
              height: 2px;
              background-color: ${scrolled || isMobileMenuOpen ? 'var(--color-primary-navy)' : 'var(--color-white)'};
              position: relative;
              transition: all 0.3s ease;
            }
            .hamburger-icon::before, .hamburger-icon::after {
              content: '';
              position: absolute;
              width: 25px;
              height: 2px;
              background-color: ${scrolled || isMobileMenuOpen ? 'var(--color-primary-navy)' : 'var(--color-white)'};
              left: 0;
              transition: all 0.3s ease;
            }
            .hamburger-icon::before { top: -8px; }
            .hamburger-icon::after { bottom: -8px; }

            .hamburger-btn.open .hamburger-icon { background-color: transparent; }
            .hamburger-btn.open .hamburger-icon::before { top: 0; transform: rotate(45deg); background-color: var(--color-primary-navy); }
            .hamburger-btn.open .hamburger-icon::after { bottom: 0; transform: rotate(-45deg); background-color: var(--color-primary-navy); }

            /* Desktop Staff Portal Button inside the mobile menu logic */
            .mobile-staff-btn { display: none; }

            @media (max-width: 1350px) and (min-width: 992px) {
              .main-nav-spaced { padding-right: 1.5rem; }
              .main-nav-spaced ul { gap: 1.5rem !important; }
              .main-nav a { font-size: 0.85rem !important; }
              .staff-portal-btn { padding: 0.8rem 1.5rem !important; }
            }

            /* --- PREMIUM MOBILE MENU --- */
            @media (max-width: 992px) {
              .top-bar-container { justify-content: center !important; text-align: center; gap: 0.5rem !important; }
              .top-bar-status { width: 100%; }
              .top-bar-contact { width: 100%; justify-content: center; }
              .hide-on-mobile { display: none !important; }
              
              .header-actions { display: none !important; }
              
              .mobile-staff-btn { display: block; margin-top: 1rem; text-align: center; }

              .hamburger-btn { display: block; margin-left: 1rem; }
              
              .main-nav-spaced {
                position: absolute;
                top: 100%;
                left: 0;
                width: 100%;
                background-color: var(--color-white);
                padding: 0;
                max-height: 0;
                overflow: hidden;
                transition: max-height 0.4s cubic-bezier(0.165, 0.84, 0.44, 1), padding 0.4s ease;
                box-shadow: 0 20px 40px rgba(0,0,0,0.1);
                justify-content: flex-start;
              }
              
              .main-nav-spaced.mobile-open {
                max-height: 100vh;
                height: 100vh;
                padding: 3rem 0 10rem 0;
                border-top: 1px solid rgba(0,0,0,0.05);
                align-items: flex-start;
                overflow-y: auto;
              }

              .main-nav-spaced ul {
                flex-direction: column;
                gap: 2rem !important;
                width: 100%;
              }

              .main-nav-spaced a {
                color: var(--color-primary-navy) !important;
                font-size: 1.25rem !important;
              }

              /* OVERRIDE TO ENSURE BUTTON TEXT IS VISIBLE */
              .main-nav-spaced li.mobile-staff-btn a {
                display: inline-block !important; 
                width: fit-content; 
                padding: 1rem 2.5rem !important; 
                background-color: var(--color-primary-navy) !important; 
                color: var(--color-white) !important; 
                border-radius: 50px !important;
                font-size: 1rem !important;
              }
            }
          `}
        </style>

        <div className="container header-container-flex">
          
          {/* DYNAMIC LOGO SWITCHER (Anchored Left) */}
          <div className="site-logo" style={{ flex: '0 0 auto', marginRight: 'auto' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
              <img 
                src={(headerBgClass === 'bg-transparent' && !isMobileMenuOpen) ? logoWhite : logoNavy} 
                alt="Nightmark Protective Services" 
                className="logo-img"
                style={{ 
                  height: logoHeight, 
                  width: 'auto', 
                  transition: 'height 0.3s ease',
                  objectFit: 'contain'
                }}
              />
            </Link>
          </div>
          
          {/* SLEEK NAVIGATION LINKS (Pushed Right) */}
          <nav className={`main-nav main-nav-spaced ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
            <ul>
              <li><Link to="/about" onClick={() => setIsMobileMenuOpen(false)}>About</Link></li>
              <li><Link to="/services" onClick={() => setIsMobileMenuOpen(false)}>Services</Link></li>
              <li><Link to="/martyns-law" onClick={() => setIsMobileMenuOpen(false)}>Martyn's Law</Link></li>
              <li><Link to="/news" onClick={() => setIsMobileMenuOpen(false)}>News</Link></li>
              <li><Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>Contact</Link></li>
              {/* This button ONLY appears on mobile, inside the menu */}
              <li className="mobile-staff-btn">
                <Link to="/staff-login" className="btn btn-pill" onClick={() => setIsMobileMenuOpen(false)}>
                  Staff Portal
                </Link>
              </li>
            </ul>
          </nav>

          {/* DESKTOP STAFF PORTAL BUTTON (Hides on Mobile) */}
          <div className="header-actions" style={{ flex: '0 0 auto' }}>
            <Link to="/staff-login" className="btn staff-portal-btn">
              Staff Portal
            </Link>
          </div>

          {/* MOBILE HAMBURGER BUTTON */}
          <button 
            className={`hamburger-btn ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Navigation"
          >
            <span className="hamburger-icon"></span>
          </button>

        </div>
      </div>
    </header>
  );
};

export default Header;